import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getCandidates = createAsyncThunk(
  "Candidates/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          // If the value is an array, append each item with the same key
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          // For non-array values, just append normally
          params.append(key, value);
        }
      }
      const queryString = params.toString();

      const { data } = await axios.get(
        `${API_URL.CANDIDATE.SLUG}?${queryString}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const addCandidate = createAsyncThunk(
  "Candidates/post",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await axios.post(`${API_URL.CANDIDATE.SLUG}`, payload);
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const editCandidate = createAsyncThunk(
  "Candidates/put",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.CANDIDATE.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteCandidate = createAsyncThunk(
  "Candidates/delete",
  async (candidateId, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const closeCandidate = createAsyncThunk(
  "Candidates/CloseCandidate",
  async (candidateId, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.CLOSE_CANDIDATE}/${candidateId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const reactivateCandidate = createAsyncThunk(
  "Candidates/reactivateCandidate",
  async (candidateId, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.REACTIVATE_CANDIDATE}/${candidateId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const blockCandidate = createAsyncThunk(
  "Candidates/blockCandidate",
  async (candidateId, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.BLOCK_CANDIDATE}/${candidateId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const changeNCStatus = createAsyncThunk(
  "Candidates/changeNCStatus",
  async (
    { ncStatusId, candidateId },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.CHANGE_NC_STATUS}/${candidateId}/${ncStatusId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const assignUserToCandidate = createAsyncThunk(
  "Candidates/assignUserToCandidate",
  async (
    { assineeId, candidateId },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.ASSIGN_USER_TO_CANDIDATE}/${candidateId}/${assineeId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      dispatch(getCandidateDetail(candidateId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const assignRecruiterToCandidate = createAsyncThunk(
  "Candidates/assignRecruiterToCandidate",
  async (
    { recruiterId, candidateId },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.ASSIGN_RECRUITER_TO_CANDIDATE}/${candidateId}/${recruiterId}`
      );
      const { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      dispatch(getCandidateDetail(candidateId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCandidateDetail = createAsyncThunk(
  "Candidates/getdetails",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.CANDIDATE.SLUG}/${payload}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateCandidateNotes = createAsyncThunk(
  "Candidates/updateCandidateNotes",
  async ({ candidateId, notes }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.UPDATE_NOTES}/${candidateId}`,
        { notes }
      );
      return { notes, data: data.data };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCandidateHistory = createAsyncThunk(
  "Candidates/getCandidateHistory",
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.HISTORY}/${candidateId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAllMessages = createAsyncThunk(
  "getAllMessages",
  async (payload, { rejectWithValue }) => {
    let apiUrl = "";
    try {
      if (payload) {
        const filteredPayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([, value]) => value !== null && value !== undefined && value !== ""
          )
        );
        const params = new URLSearchParams(filteredPayload);
        const queryString = params.toString();
        apiUrl = `${API_URL.CANDIDATE.GET_MESSAGE_LIST}?${queryString}`;
      } else {
        apiUrl = `${API_URL.CANDIDATE.GET_MESSAGE_LIST}`;
      }
      const { data } = await axios.get(apiUrl);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const sendMessage = createAsyncThunk(
  "sendMessage",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SEND_MESSAGE}/${candidateId}`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getTemplate = createAsyncThunk(
  "Candidates/getTemplate",
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.TEMPLATE}/${candidateId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const candidateSlice = createSlice({
  name: "candidate",
  initialState: {
    candidate_list: [],
    candidate_details: null,
    current_candidate_values: null,
    all_message_list: null,
    candidate_history: null,
    active_payload: null,
    template_loading: false,
    message_loading: false,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_candidate_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCandidateDetails(state) {
      state.candidate_details = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCandidates.fulfilled, (state, action) => {
      state.candidate_list = action.payload.list;
    });
    builder.addCase(getCandidateDetail.fulfilled, (state, action) => {
      state.candidate_details = action.payload;
    });
    builder.addCase(updateCandidateNotes.fulfilled, (state, action) => {
      if (state.candidate_details) {
        state.candidate_details.notes = action.payload.notes;
      }
    });
    builder.addCase(getAllMessages.pending, (state, action) => {
      state.all_message_list = null;
      state.message_loading = true;
    });
    builder.addCase(getAllMessages.fulfilled, (state, action) => {
      state.all_message_list = action.payload;
      state.message_loading = false;
    });
    builder.addCase(getAllMessages.rejected, (state, action) => {
      state.all_message_list = null;
      state.message_loading = false;
    });
    builder.addCase(getCandidateHistory.fulfilled, (state, action) => {
      state.candidate_history = action.payload;
    });
    builder.addCase(getTemplate.pending, (state) => {
      state.template_loading = true;
    });
    builder.addCase(getTemplate.fulfilled, (state) => {
      state.template_loading = false;
    });
    builder.addCase(getTemplate.rejected, (state) => {
      state.template_loading = false;
    });
  },
});

export const { setCurrentValues, setActivePayload, resetCandidateDetails } =
  candidateSlice.actions;
export default candidateSlice.reducer;
